import { useState } from 'react';
import { Badge, Card, Confirm, ErrorBox, Loading, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { UDF_TABLE_LABELS } from '../../labels';
import type { UdfInstallResult, UdfState, UdfStatus } from '../../types';
import type { TenantTabProps } from '../TenantDetail';

const STATE_BADGE: Record<UdfState, { tone: 'ok' | 'bad' | 'warn'; label: string }> = {
  ok: { tone: 'ok', label: 'Correcto' },
  missing: { tone: 'bad', label: 'Falta' },
  different: { tone: 'warn', label: 'No coincide' },
};

const TABLE_ORDER = ['OPOR', 'POR1', 'OCRD'];

export function UdfCard({ slug, detail }: TenantTabProps): JSX.Element {
  const toast = useToast();
  const [fields, setFields] = useState<UdfStatus[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [installing, setInstalling] = useState(false);
  const [install, setInstall] = useState<UdfInstallResult | null>(null);

  const check = async (): Promise<void> => {
    setChecking(true);
    setError(null);
    try {
      const res = await api.get<{ fields: UdfStatus[] }>(`${tenantPath(slug)}/udfs`);
      setFields(res.fields);
    } catch (err) {
      setError(err);
    } finally {
      setChecking(false);
    }
  };

  const runInstall = async (): Promise<void> => {
    const res = await api.post<UdfInstallResult>(`${tenantPath(slug)}/udfs/install`);
    setInstall(res);
    setFields(res.status);
    if (res.failed.length) toast(`${res.created.length} campos creados, ${res.failed.length} con error`, 'bad');
    else toast(`${res.created.length} campos creados en SAP`, 'ok');
  };

  const missing = fields?.filter((f) => f.state === 'missing').length ?? 0;
  const different = fields?.filter((f) => f.state === 'different').length ?? 0;
  const noCompany = detail.tenant.companies.length === 0;

  return (
    <Card
      title={
        <div>
          <h2>Campos de usuario U_EDI_ en SAP</h2>
          <p className="subtle">Los campos donde Provalia guarda en SAP la aceptación, fechas, albaranes y evaluación.</p>
        </div>
      }
      actions={
        <>
          <button className="btn" onClick={() => void check()} disabled={checking || noCompany}>
            {checking ? 'Comprobando…' : fields ? 'Volver a comprobar' : 'Comprobar'}
          </button>
          {fields && missing > 0 ? (
            <button className="btn primary" onClick={() => setInstalling(true)}>
              Crear los que faltan ({missing})
            </button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        <div className="callout warn">
          SAP no deja crear campos en una tabla que otro usuario tiene abierta: hazlo con los usuarios fuera de SAP. Nunca se modifica ni
          borra un campo existente.
        </div>
        {noCompany ? <p className="muted">Añade primero una sociedad: la comprobación inicia sesión en su SAP.</p> : null}
        {error ? <ErrorBox error={error} /> : null}
        {install ? <InstallResult result={install} /> : null}
        {checking && !fields ? <Loading label="Iniciando sesión en SAP y leyendo los campos…" /> : null}
        {fields ? (
          <>
            <div className="row">
              {missing === 0 && different === 0 ? <Badge tone="ok">Todos los campos están bien</Badge> : null}
              {missing > 0 ? <Badge tone="bad">{missing} faltan</Badge> : null}
              {different > 0 ? <Badge tone="warn">{different} no coinciden: se corrigen a mano en SAP</Badge> : null}
            </div>
            <UdfTable fields={fields} />
          </>
        ) : !checking && !noCompany && !error ? (
          <p className="muted">Pulsa «Comprobar» para iniciar sesión en el SAP del cliente y ver qué campos tiene.</p>
        ) : null}
      </div>

      {installing ? (
        <Confirm
          title="Crear los campos que faltan"
          confirmLabel={`Crear ${missing} campos`}
          message={
            <div className="stack sm">
              <p>Se crearán {missing} campos de usuario en el SAP del cliente, uno a uno. Los que ya existen no se tocan.</p>
              <p className="muted">Antes, asegúrate de que nadie tiene abiertas en SAP las tablas de pedidos de compra ni de interlocutores.</p>
            </div>
          }
          onClose={() => setInstalling(false)}
          onConfirm={runInstall}
        />
      ) : null}
    </Card>
  );
}

function UdfTable({ fields }: { fields: UdfStatus[] }): JSX.Element {
  const tables = [...TABLE_ORDER, ...new Set(fields.map((f) => f.table).filter((t) => !TABLE_ORDER.includes(t)))];
  return (
    <div className="table-wrap bordered">
      <table className="table">
        <thead>
          <tr>
            <th>Campo</th>
            <th>Descripción</th>
            <th>Estado</th>
          </tr>
        </thead>
        {tables.map((table) => {
          const rows = fields.filter((f) => f.table === table);
          if (rows.length === 0) return null;
          return (
            <tbody key={table}>
              <tr className="group-row">
                <th colSpan={3}>
                  {UDF_TABLE_LABELS[table] ?? table} <span className="mono subtle">{table}</span>
                </th>
              </tr>
              {rows.map((f) => (
                <tr key={`${f.table}.${f.field}`}>
                  <td className="mono nowrap">{f.field}</td>
                  <td>{f.description.replace(/^Provalia: /, '')}</td>
                  <td>
                    <Badge tone={STATE_BADGE[f.state].tone}>{STATE_BADGE[f.state].label}</Badge>
                    {f.note ? <div className="subtle cell-note">{f.note}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

function InstallResult({ result }: { result: UdfInstallResult }): JSX.Element {
  return (
    <div className={`callout ${result.failed.length ? 'bad' : 'ok'}`} role="status">
      <div className="title">
        {result.created.length} creados{result.failed.length ? `, ${result.failed.length} con error` : ''}
      </div>
      {result.created.length ? (
        <p>
          Creados: <span className="mono">{result.created.join(', ')}</span>
        </p>
      ) : null}
      {result.failed.length ? (
        <ul className="plain-list">
          {result.failed.map((f) => (
            <li key={f.field}>
              <span className="mono">{f.field}</span>: {f.error}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
